import { cached } from "@glimmer/tracking";
import { warn } from "@ember/debug";
import { configNavForPlugin } from "discourse/lib/admin-plugin-config-nav";
import { adminRouteValid } from "discourse/lib/admin-utilities";
import { getOwnerWithFallback } from "discourse/lib/get-owner";
import getURL from "discourse/lib/get-url";
import PreloadStore from "discourse/lib/preload-store";
import BaseCustomSidebarPanel from "discourse/lib/sidebar/base-custom-sidebar-panel";
import BaseCustomSidebarSection from "discourse/lib/sidebar/base-custom-sidebar-section";
import BaseCustomSidebarSectionLink from "discourse/lib/sidebar/base-custom-sidebar-section-link";
import { getReviewBadgeText } from "discourse/lib/sidebar/helpers/review-badge-helper";
import { ADMIN_PANEL } from "discourse/lib/sidebar/panels";
import { applyValueTransformer } from "discourse/lib/transformer";
import {
  escapeExpression,
  stripDiscoursePrefix,
} from "discourse/lib/utilities";
import I18n, { i18n } from "discourse-i18n";

let additionalAdminSidebarSectionLinks = {};

// Section headers can only render links, so anything that is an action rather
// than a destination is resolved here by the id the nav map declares.
const SECTION_HEADER_ACTIONS = {
  design_wizard: {
    available: ({ currentUser }) => currentUser?.can_run_design_wizard,
    action: ({ designWizard, router }) =>
      designWizard.launch({ returnUrl: router.currentURL }),
  },
};

// For testing.
export function clearAdditionalAdminSidebarSectionLinks() {
  additionalAdminSidebarSectionLinks = {};
}

class SidebarAdminSectionLink extends BaseCustomSidebarSectionLink {
  constructor({
    adminSidebarNavLink,
    adminSidebarStateManager,
    router,
    currentUser,
  }) {
    super(...arguments);
    this.router = router;
    this.currentUser = currentUser;
    this.adminSidebarNavLink = adminSidebarNavLink;
    this.adminSidebarStateManager = adminSidebarStateManager;
  }

  get name() {
    return this.adminSidebarNavLink.name;
  }

  get classNames() {
    return "admin-sidebar-nav-link";
  }

  get route() {
    return this.adminSidebarNavLink.route;
  }

  get href() {
    if (this.adminSidebarNavLink.href) {
      return getURL(this.adminSidebarNavLink.href);
    }
  }

  get query() {
    return this.adminSidebarNavLink.query;
  }

  get models() {
    return this.adminSidebarNavLink.routeModels;
  }

  get text() {
    const text = this.adminSidebarNavLink.label
      ? i18n(this.adminSidebarNavLink.label, {
          translatedFallback: this.adminSidebarNavLink.text,
        })
      : this.adminSidebarNavLink.text;
    return stripDiscoursePrefix(text);
  }

  get prefixType() {
    return "icon";
  }

  get prefixValue() {
    return this.adminSidebarNavLink.icon;
  }

  get title() {
    return this.adminSidebarNavLink.text;
  }

  get currentWhen() {
    // This is needed because the setting route is underneath /admin/plugins/:plugin_id,
    // but is not a child route of the plugin routes themselves. E.g. discourse-ai
    // for the plugin ID has its own nested routes defined in the plugin.
    if (this.router.currentRoute.name === "adminPlugins.show.settings") {
      if (
        this.adminSidebarNavLink.route?.split(".").last ===
        this.router.currentRoute.parent.params.plugin_id
      ) {
        return this.router.currentRoute.name;
      }
    }
    if (this.adminSidebarNavLink.currentWhen) {
      return this.adminSidebarNavLink.currentWhen;
    }
  }

  get keywords() {
    return (
      this.adminSidebarStateManager.keywords[this.adminSidebarNavLink.name] || {
        navigation: [],
      }
    );
  }

  get badgeText() {
    if (this.adminSidebarNavLink.name === "admin_review") {
      return getReviewBadgeText(this.currentUser);
    }
  }

  get suffixType() {
    if (this.#hasUnseenFeatures || this.#hasNewUpcomingChanges) {
      return "icon";
    }
  }

  get suffixValue() {
    if (this.#hasUnseenFeatures || this.#hasNewUpcomingChanges) {
      return "circle";
    }
  }

  get suffixCSSClass() {
    if (this.#hasUnseenFeatures || this.#hasNewUpcomingChanges) {
      return "admin-sidebar-nav-link__dot";
    }
  }

  get #hasUnseenFeatures() {
    return (
      this.adminSidebarNavLink.name === "admin_whats_new" &&
      this.currentUser.hasUnseenFeatures
    );
  }

  get #hasNewUpcomingChanges() {
    return (
      this.adminSidebarNavLink.name === "admin_upcoming_changes" &&
      this.currentUser.hasNewUpcomingChanges
    );
  }
}

function defineAdminSection(
  adminNavSectionData,
  adminSidebarStateManager,
  router,
  currentUser,
  headerActionContext
) {
  const AdminNavSection = class extends BaseCustomSidebarSection {
    constructor() {
      super(...arguments);
      this.adminNavSectionData = adminNavSectionData;
      this.hideSectionHeader = adminNavSectionData.hideSectionHeader;
      this.adminSidebarStateManager = adminSidebarStateManager;
    }

    get sectionLinks() {
      return this.adminNavSectionData.links;
    }

    get name() {
      return `${ADMIN_PANEL}-${this.adminNavSectionData.name}`;
    }

    get title() {
      return this.adminNavSectionData.text;
    }

    get text() {
      return this.adminNavSectionData.label
        ? i18n(this.adminNavSectionData.label)
        : this.adminNavSectionData.text;
    }

    get links() {
      return this.sectionLinks.map(
        (sectionLinkData) =>
          new SidebarAdminSectionLink({
            adminSidebarNavLink: sectionLinkData,
            adminSidebarStateManager: this.adminSidebarStateManager,
            router,
            currentUser,
          })
      );
    }

    get displaySection() {
      return true;
    }

    get collapsedByDefault() {
      return this.adminNavSectionData.name !== "root";
    }

    get actionsIcon() {
      return this.#headerActions.length > 1
        ? "ellipsis-vertical"
        : this.#headerActions[0]?.icon;
    }

    @cached
    get actions() {
      return this.#headerActions.map((headerAction) => ({
        id: headerAction.id,
        title: i18n(headerAction.label),
        action: () =>
          SECTION_HEADER_ACTIONS[headerAction.id].action(headerActionContext),
      }));
    }

    // a header action is how its section's own feature is reached, so unlike
    // the personal sidebar's editing controls it cannot wait for a hover
    get persistentActions() {
      return this.#headerActions.length > 0;
    }

    get #headerActions() {
      return (this.adminNavSectionData.headerActions ?? []).filter(
        (headerAction) =>
          SECTION_HEADER_ACTIONS[headerAction.id]?.available(
            headerActionContext
          )
      );
    }
  };

  return AdminNavSection;
}

// This is used for a plugin API.
export function addAdminSidebarSectionLink(sectionName, link) {
  if (!additionalAdminSidebarSectionLinks.hasOwnProperty(sectionName)) {
    additionalAdminSidebarSectionLinks[sectionName] = [];
  }

  // make the name extra-unique
  link.name = `admin_additional_${sectionName}_${link.name}`;

  if (!link.href && !link.route) {
    // eslint-disable-next-line no-console
    console.debug(
      "[AdminSidebar]",
      `Custom link ${sectionName}_${link.name} must have either href or route`
    );
    return;
  }

  if (!link.label && !link.text) {
    // eslint-disable-next-line no-console
    console.debug(
      "[AdminSidebar]",
      `Custom link ${sectionName}_${link.name} must have either label (which is an I18n key) or text`
    );
    return;
  }

  // label must be valid, don't want broken [XYZ translation missing]
  if (
    link.label &&
    i18n(link.label) === I18n.missingTranslation(link.label, null, {})
  ) {
    // eslint-disable-next-line no-console
    console.debug(
      "[AdminSidebar]",
      `Custom link ${sectionName}_${link.name} must have a valid I18n label, got ${link.label}`
    );
    return;
  }

  additionalAdminSidebarSectionLinks[sectionName].push(link);
}

function pluginAdminRouteLinks(router) {
  const pluginLinks = (PreloadStore.get("visiblePlugins") || [])
    .filter((plugin) => {
      if (!plugin.admin_route || !plugin.enabled) {
        return false;
      }

      // Check if the admin route is valid, if it is not the whole admin
      // interface can break because of this. This can be the case for things
      // like ad blockers stopping plugin JS from loading.
      if (adminRouteValid(router, plugin.admin_route)) {
        return true;
      } else {
        warn(
          `[AdminSidebar] Could not find admin route for ${plugin.name}, route was ${plugin.admin_route.full_location}. This could be caused by an ad blocker.`,
          { id: "discourse.admin-sidebar:plugin-admin-route-links" }
        );
        return false;
      }
    })
    .map((plugin) => {
      const pluginAdminRoute = plugin.admin_route.use_new_show_route
        ? `adminPlugins.show`
        : `adminPlugins.${plugin.admin_route.location}`;
      const pluginConfigNav = configNavForPlugin(plugin.name);

      let pluginNavLinks = [];
      if (pluginConfigNav) {
        if (Array.isArray(pluginConfigNav.links)) {
          pluginNavLinks = [...pluginConfigNav.links];
        }

        if (pluginNavLinks.length) {
          pluginNavLinks = pluginNavLinks
            .map((link) => {
              if (!link.icon) {
                link.icon = "gear";
              }
              if (link.route !== `${pluginAdminRoute}.${plugin.name}`) {
                link.routeModels = [plugin.name];
                return link;
              } else {
                return;
              }
            })
            .filter((item) => item != null);
        }
      }

      return {
        name: `admin_plugin_${plugin.admin_route.location}`,
        route: pluginAdminRoute,
        routeModels: plugin.admin_route.use_new_show_route
          ? [plugin.admin_route.location]
          : [],
        label: plugin.admin_route.label,
        text: plugin.humanized_name,
        icon: applyValueTransformer("admin-plugin-icon", "gear", {
          pluginId: plugin.name,
        }),
        description: plugin.description,
        links: pluginNavLinks,
      };
    });

  return pluginLinks.sort((a, b) => {
    const aText = stripDiscoursePrefix(
      a.label ? i18n(a.label, { translatedFallback: a.text }) : a.text
    );
    const bText = stripDiscoursePrefix(
      b.label ? i18n(b.label, { translatedFallback: b.text }) : b.text
    );

    return aText.localeCompare(bText) || a.name.localeCompare(b.name);
  });
}

function installedPluginsLinkKeywords() {
  return (PreloadStore.get("visiblePlugins") || []).map(
    (plugin) => plugin.name
  );
}

export default class AdminSidebarPanel extends BaseCustomSidebarPanel {
  key = ADMIN_PANEL;
  hidden = true;
  displayHeader = true;
  expandActiveSection = true;
  scrollActiveLinkIntoView = true;

  @cached
  get sections() {
    const currentUser = getOwnerWithFallback(this).lookup(
      "service:current-user"
    );
    const siteSettings = getOwnerWithFallback(this).lookup(
      "service:site-settings"
    );
    const site = getOwnerWithFallback(this).lookup("service:site");
    const store = getOwnerWithFallback(this).lookup("service:store");
    const router = getOwnerWithFallback(this).lookup("service:router");
    const session = getOwnerWithFallback(this).lookup("service:session");
    const designWizard = getOwnerWithFallback(this).lookup(
      "service:design-wizard"
    );

    this.adminSidebarStateManager = getOwnerWithFallback(this).lookup(
      "service:admin-sidebar-state-manager"
    );

    this.adminNavManager = getOwnerWithFallback(this).lookup(
      "service:admin-nav-manager"
    );

    this.adminNavManager.resetNavMap();

    if (!session.get("safe_mode")) {
      this.adminNavManager.amendLinksToSection(
        "plugins",
        pluginAdminRouteLinks(router)
      );

      this.adminSidebarStateManager.setLinkKeywords(
        "admin_installed_plugins",
        installedPluginsLinkKeywords()
      );

      if (site.admin_config_login_routes) {
        this.adminNavManager.amendLinksToSubSection(
          "admin_login",
          site.admin_config_login_routes.map((routeName) => ({
            name: `admin_login_${routeName}`,
            route: `adminConfig.login.plugin-tab`,
            routeModels: [routeName],
            icon: "unlock",
            label: `admin.config.login.sub_pages.${routeName}`,
            settings_area: routeName,
          }))
        );
      }
    }

    // Mods cannot access themes
    if (currentUser.admin) {
      store.findAll("theme").then((themes) => {
        this.adminSidebarStateManager.setLinkKeywords(
          "admin_themes_and_components",
          themes.content
            .filter((theme) => !theme.component)
            .map((theme) => theme.name)
        );
        this.adminSidebarStateManager.setLinkKeywords(
          "admin_themes_and_components",
          themes.content
            .filter((theme) => theme.component)
            .map((theme) => theme.name)
        );
      });
    }

    if (siteSettings.enable_form_templates) {
      this.adminNavManager.amendLinksToSection("appearance", [
        {
          name: "admin_customize_form_templates",
          route: "adminCustomizeFormTemplates",
          label: "admin.config.form_templates.title",
          description: "admin.config.form_templates.header_description",
          icon: "list",
        },
      ]);
    }

    if (siteSettings.enable_gifs) {
      this.adminNavManager.amendLinksToSection("appearance", [
        {
          name: "admin_gifs",
          route: "adminConfig.gifs.settings",
          label: "admin.config.gifs.title",
          description: "admin.config.gifs.header_description",
          icon: "gif",
          settings_area: "gifs",
        },
      ]);
    }

    this.adminNavManager.amendLinksToSection("root", [
      {
        name: "admin_upcoming_changes",
        route: "adminConfig.upcomingChanges",
        label: "admin.config.upcoming_changes.title",
        description: "admin.config.upcoming_changes.header_description",
        icon: "flask",
        keywords: "admin.config.upcoming_changes.keywords",
      },
    ]);

    for (const [sectionName, additionalLinks] of Object.entries(
      additionalAdminSidebarSectionLinks
    )) {
      if (additionalLinks.length) {
        this.adminNavManager.amendLinksToSection(sectionName, additionalLinks);
      }
    }

    return this.adminNavManager.filteredNavMap.map((section) => {
      section.links.forEach((link) => {
        if (link.keywords) {
          this.adminSidebarStateManager.setLinkKeywords(
            link.name,
            i18n(link.keywords).split("|")
          );
        }
      });

      return defineAdminSection(
        section,
        this.adminSidebarStateManager,
        router,
        currentUser,
        { currentUser, designWizard, router }
      );
    });
  }

  get searchable() {
    const currentUser = getOwnerWithFallback(this).lookup(
      "service:current-user"
    );
    return currentUser.admin;
  }

  get filterable() {
    const currentUser = getOwnerWithFallback(this).lookup(
      "service:current-user"
    );
    return !currentUser.admin && currentUser.moderator;
  }

  get onSearchClick() {
    getOwnerWithFallback(this)
      .lookup("service:modal")
      .show(this.adminSidebarStateManager.modals.adminSearch);
  }

  filterNoResultsDescription(filter) {
    const escapedFilter = escapeExpression(filter);

    i18n("sidebar.no_results.description_admin_search", {
      filter: escapedFilter,
    });
  }
}
